/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import PhoneNumber from '../../../components/PhoneNumber/Index';
import * as deptSvc from '../../../services/clientAdmin/departmentServices';
import Modal from '../../../components/modal/Index';

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .trim()
    .min(3, 'First name must be at least 3 characters')
    .matches(/^[A-Za-z\s]+$/, 'First name must contain only letters')
    .required('First name is required'),

  lastName: Yup.string()
    .trim()
    .matches(/^[A-Za-z\s]+$/, 'Last name must contain only letters and spaces')
    .required('Last name is required'),
  email: Yup.string()
    .email('Please enter a valid email')
    .required('Email is required'),
  phone: Yup.string(),
  departmentId: Yup.string(),
  teamId: Yup.string().when('departmentId', {
    is: (departmentId) => !!departmentId,
    then: (schema) => schema.required('Team is required when department is selected'),
    otherwise: (schema) => schema.notRequired(),
  }),

  employeeRollNumber: Yup.string()
    .matches(/^[0-9]*$/, 'Employee roll number must contain numbers only'), notes: Yup.string(),
});

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  departmentId: '',
  teamId: '',
  employeeRollNumber: '',
  notes: '',
};

const fieldCls = (hasError) =>
  `w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${hasError ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
  }`;

export default function AddLearnerDrawer({ isOpen, onClose, onSubmit, editData }) {
  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showDiscard, setShowDiscard] = useState(false);
  const firstNameRef = useRef(null);

  // Support edit mode — if editData passed, pre-fill form
  const formInitialValues = editData
    ? {
      firstName: editData.firstName || '',
      lastName: editData.lastName || '',
      email: editData.email || '',
      phone: editData.phone || '',
      departmentId: editData.departmentId || '',
      teamId: editData.teamId || '',
      employeeRollNumber: editData.employeeRollNumber || '',
      notes: editData.notes || '',
    }
    : initialValues;

  useEffect(() => {
    if (isOpen) {
      deptSvc.getDepartments().then((r) => {
        if (r.ok) setDepartments((r.data || []).filter((d) => d.isActive));
      });
      // Load teams if editing and dept already set
      if (editData?.departmentId) {
        setTeamsLoading(true);
        deptSvc.getTeams(editData.departmentId).then((r) => {
          if (r.ok) setTeams((r.data || []).filter((t) => t.isActive));
          setTeamsLoading(false);
        });
      }
      setTimeout(() => firstNameRef.current?.focus(), 50);
    }
  }, [isOpen]);

  function getTeamPlaceholder(departmentId, isLoading) {
    if (isLoading) return 'Loading teams…';
    if (!departmentId) return 'Select a department first';
    return 'No team';
  }

  const handleDeptChange = async (deptId, setFieldValue) => {
    setFieldValue('departmentId', deptId);
    setFieldValue('teamId', '');
    setTeams([]);
    if (!deptId) return;
    setTeamsLoading(true);
    const r = await deptSvc.getTeams(deptId);
    if (r.ok) setTeams((r.data || []).filter((t) => t.isActive));
    setTeamsLoading(false);
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    setSubmitError('');
    try {
      await onSubmit(values);
    } catch (error) {
      setSubmitError(error?.data?.message || error?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelClick = (dirty) => {
    if (dirty) {
      setShowDiscard(true);
    } else {
      handleClose();
    }
  };

  const handleClose = () => {
    setSubmitError('');
    setTeams([]);
    setShowDiscard(false);
    onClose();
  };
  const submitLabel = editData ? 'Save Changes' : 'Add Learner';

  return (
    <>
      <Modal title={editData ? 'Edit Learner' : 'Add Learner'} open={isOpen} toggle={() => handleCancelClick(true)}>
        <Formik
          initialValues={formInitialValues}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue, dirty }) => (
            <Form className="space-y-6">

              {/* PERSONAL DETAILS */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                  Personal Details
                </h3>
                <div className="space-y-4">

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-900 mb-1.5">
                        First Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        ref={firstNameRef}
                        id="firstName"
                        type="text"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="e.g. Arjun"
                        className={fieldCls(touched.firstName && errors.firstName)}
                      />
                      {touched.firstName && errors.firstName && (
                        <p className="text-red-500 text-xs mt-1">⚠ {errors.firstName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-900 mb-1.5">
                        Last Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="lastName"
                        type="text"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="e.g. Mehta"
                        className={fieldCls(touched.lastName && errors.lastName)}
                      />
                      {touched.lastName && errors.lastName && (
                        <p className="text-red-500 text-xs mt-1">⚠ {errors.lastName}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-1.5">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      name="email"
                      autoComplete="off"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. arjun@example.com"
                      className={fieldCls(touched.email && errors.email)}
                    />
                    {touched.email && errors.email && (
                      <p className="text-red-500 text-xs mt-1">⚠ {errors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-1.5">
                      Phone Number
                    </label>
                    <PhoneNumber
                      fullWidth
                      country="in"
                      name="phone"
                      specialLabel=""
                      value={values.phone}
                      onChange={(value) => setFieldValue('phone', value)}
                      inputProps={{ autoFocus: false }}
                    />
                  </div>


                </div>
              </div>

              {/* ORGANISATION */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                  Organisation
                </h3>
                <div className="space-y-4">

                  <div>
                    <label htmlFor="departmentId" className="block text-sm font-medium text-gray-900 mb-1.5">
                      Department
                    </label>
                    <select
                      id="departmentId"
                      name="departmentId"
                      value={values.departmentId}
                      onChange={(e) => handleDeptChange(e.target.value, setFieldValue)}
                      onBlur={handleBlur}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">No department</option>
                      {departments.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="teamId" className="block text-sm font-medium text-gray-900 mb-1.5">
                      Team
                      {values.departmentId && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      id="teamId"
                      name="teamId"
                      value={values.teamId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={!values.departmentId || teamsLoading}
                      className={`w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all bg-white ${!values.departmentId ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >

                      <option value="">{getTeamPlaceholder(values.departmentId, teamsLoading)}</option>
                      {teams.map((t) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                    {touched.teamId && errors.teamId && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.teamId}
                      </p>
                    )}
                  </div>

                </div>
              </div>

              {/* INTERNAL FIELDS */}
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-4">
                  Internal Fields
                </h3>
                <div className="space-y-4">

                  <div>
                    <label htmlFor="employeeRollNumber" className="block text-sm font-medium text-gray-900 mb-1.5">
                      Roll Number
                    </label>
                    <input
                      id="employeeRollNumber"
                      type="number"
                      min="0"
                      name="employeeRollNumber"
                      value={values.employeeRollNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="e.g. 20260001"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all"
                    />
                    {touched.employeeRollNumber && errors.employeeRollNumber && (
                      <p className="text-red-500 text-xs mt-1">⚠ {errors.employeeRollNumber}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-900 mb-1.5">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={4}
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Internal notes about this learner..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all resize-none"
                    />
                  </div>

                </div>
              </div>

              {/* ERROR */}
              {submitError && (
                <p className="text-red-500 text-xs">⚠ {submitError}</p>
              )}

              {/* FOOTER */}
              <div className="flex gap-3 justify-end pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => handleCancelClick(dirty)}
                  className="px-5 py-2.5 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSubmitting && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {isSubmitting ? 'Saving…' : submitLabel}                </button>
              </div>

            </Form>
          )}
        </Formik>
      </Modal>

      {showDiscard && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Discard changes?</h2>
            <p className="text-sm text-gray-500 mb-6">
              You have unsaved changes. They will be lost if you close now.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDiscard(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Keep Editing
              </button>
              <button
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors"
              >
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}